/**
 * Segment Analytics module for Javascript.
 *
 * This module is heavily based on Segment's own snippet, but in ES6.
 * See: https://github.com/segmentio/snippet/blob/master/template/snippet.js
 *
 * Segment's analytic script can be loaded in both ways:
 *   - By adding the normal segment snippet to the HTML
 *   - By doing Analytics.load(<key>) in JS
 *
 * Notes:
 *   - If Segment happens to be loaded/initialized twice, we'll have a console error
 *     but not override the initial instance.
 *   - Attempting to call any analytics methods prior to `Analytics.load(<key>)` will
 *     not result in an error since all methods are stubbed. When the load call happens,
 *     any pending events will be sent out. This is the same behavior of Segment's own
 *     snippet.
 *   - If Segment's analytics script is not loaded (eg: we're a the development
 *     environment), any events will be pushed to `window.analytics` but not sent off
 *     anywhere (see prior point). If we need to verify events are being called correctly,
 *     we can just console log `window.analytics`.
 *
 * IMPORTANT:
 *   - Do NOT assign `window.analytics` to a variable in order to save yourself some typing.
 *     When Segment's full analytics script loads it'll redefine `window.analytics`.
 *   - While an ES6 Proxy would make it much shorter and easier to add functionality
 *     to, we can't use that. Not all latest browsers have Proxy implemented (looking at
 *     you, IE), and Babel will not transpile them, because they requires support on the
 *     engine level.
 */

const SegmentAnalytics = function() {
  const returnObject = {};

  // Available Segment's analytics methods
  const segmentMethods = [
    'trackSubmit',
    'trackClick',
    'trackLink',
    'trackForm',
    'pageview',
    'identify',
    'reset',
    'group',
    'track',
    'ready',
    'alias',
    'debug',
    'page',
    'once',
    'off',
    'on'
  ];

  const initialize = () => {
    window.analytics = window.analytics || [];

    buildStubsAndMethods();
  };

  /**
   * Adding Segment's methods to our module's export so they can be called from anywhere.
   * Building Segment's analytics stubs to be used while Segment's full script isn't loaded.
   */
  const buildStubsAndMethods = () => {
    segmentMethods.forEach((name) => {
      // Don't override Segment's analytics methods with stubs if already initialized.
      if (!window.analytics.initialize) {
        window.analytics[name] = (...args) => {
          window.analytics.push([name, ...args]);
        };
      }

      // Our proxy methods
      returnObject[name] = (...args) => {
        window.analytics[name](...args);
      };
    });
  };

  // Load segment's full analytics script into the DOM asyncronously.
  const loadScriptIntoDOM = (key) => {
    const script = document.createElement('script');

    script.type = 'text/javascript';
    script.async = true;
    script.src = `${document.location.protocol}//cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`;

    const firstScriptElement = document.getElementsByTagName('script')[0];
    firstScriptElement.parentNode.insertBefore(script, firstScriptElement);
  };

  returnObject.load = (key, options) => {
    if (window.analytics.initialize || window.analytics.invoked) {
      /* eslint-disable no-console */
      if (window.console && console.error) {
        console.error('Segment was already loaded.');
      }
      /* eslint-enable no-console */
      return;
    }

    window.analytics.invoked = true;
    window.analytics._loadOptions = options;

    loadScriptIntoDOM(key);
  };

  initialize();

  return returnObject;
}();

export default SegmentAnalytics;
